import React, { useEffect, useRef, useState } from "react";
import {
  Download,
  FileText,
  AlertTriangle,
  CheckCircle,
  Loader2,
  RefreshCw,
  ExternalLink,
  Save,
  ShieldCheck,
  ShieldAlert,
  Clock,
  X,
} from "lucide-react";
import {
  useOrderArrayReport,
  useArrayReportStatus,
  useFetchArrayFileMutation,
} from "../../services/queries/arrayReportQueries";
import {
  ArrayError,
  ArrayReportType,
  OrderArrayReportResponse,
  isReportReady,
} from "../../services/arrayIntegration";
import { useToast } from "../../contexts/ToastContext";
import { useUploadCreditBlockFile } from "../../services/queries/creditBlockFileQueries";
import { getRelationshipTypeKeyForCategory } from "../../config/gobodoCreditBlockFileRelations";
import {
  useApproveArrayRequest,
  useCurrentArrayRequest,
  useIsArrayApprover,
  usePersistArrayMetadata,
  useRecallArrayRequest,
  useRejectArrayRequest,
  useSubmitArrayRequest,
} from "../../services/arrayCreditReport/queries";

interface Props {
  arrayClientKey: string;
  clientName: string;
  /** Gobodo client id — required for approval workflow + Files-tab writeback. */
  clientId?: string;
  /** Last 4 of client SSN — used to password-protect the PDF before upload,
   *  per the ops doc "password-protect before sending" requirement. */
  ssnLast4?: string;
}

function errorCopy(err: ArrayError): {
  title: string;
  detail: string;
  retry?: "offCycle" | "plain";
} {
  switch (err.code) {
    case "unverified_user":
      return {
        title: "Client not verified with Array",
        detail:
          "Please verify the user in Salesforce before pulling a credit report.",
      };
    case "report_expired":
      return {
        title: "Report expired",
        detail:
          "The last report is older than 30 days. Request a new pull to continue.",
        retry: "offCycle",
      };
    case "unknown_order":
      return {
        title: "Order not found",
        detail: "The order state is stale. Try again.",
        retry: "plain",
      };
    case "auth_misconfigured":
      return {
        title: "Array auth misconfigured",
        detail: "Contact engineering — API key may have rotated.",
      };
    case "saved_after_order":
      return {
        title: "Order placed but not saved",
        detail:
          "Array accepted the order but our record failed. Retrying is safe — cache will carry forward.",
        retry: "plain",
      };
    case "validation":
      return {
        title: "Invalid client key",
        detail: "The arrayClientKey is missing or malformed.",
      };
    case "report_not_ready":
      return {
        title: "Report not ready yet",
        detail: "The status said done but the file is not available. Retrying…",
        retry: "plain",
      };
    default:
      return { title: "Credit pull failed", detail: err.message };
  }
}

const APPROVERS = ["Kathleen", "Melanie", "Dr. Cris"];

/** Extract a usable message from Gobodo SDK errors, network errors, plain strings, etc. */
function describeError(err: unknown): string {
  if (!err) return "unknown error";
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  const e = err as {
    status?: number;
    statusCode?: number;
    message?: string;
    body?: { message?: string; error?: string } | string;
    response?: { status?: number; data?: unknown; body?: unknown };
  };
  const status = e.status ?? e.statusCode ?? e.response?.status;
  const bodyMsg =
    (typeof e.body === "object" && (e.body?.message || e.body?.error)) ||
    (typeof e.body === "string" ? e.body : undefined);
  const responseBody = e.response?.data ?? e.response?.body;
  const responseMsg =
    (responseBody &&
    typeof responseBody === "object" &&
    "message" in responseBody
      ? String((responseBody as { message: unknown }).message)
      : undefined) ??
    (typeof responseBody === "string" ? responseBody : undefined);
  const plain = e.message || bodyMsg || responseMsg;
  const parts: string[] = [];
  if (status) parts.push(`HTTP ${status}`);
  if (plain) parts.push(plain);
  if (parts.length === 0) {
    try {
      return JSON.stringify(err).slice(0, 300);
    } catch {
      return String(err);
    }
  }
  return parts.join(" — ");
}

const ArrayCreditPullPanel: React.FC<Props> = ({
  arrayClientKey,
  clientName,
  clientId,
  ssnLast4,
}) => {
  const { success, error: toastError, info } = useToast();
  const [order, setOrder] = useState<OrderArrayReportResponse | undefined>(
    undefined,
  );
  const [startedAt, setStartedAt] = useState<number | undefined>(undefined);
  const [confirmForce, setConfirmForce] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [savedArtifacts, setSavedArtifacts] = useState<Set<string>>(new Set());
  const [savingArtifact, setSavingArtifact] = useState<ArrayReportType | null>(
    null,
  );
  // Track in-flight saves synchronously — state updates are async, but we
  // need the next effect fire to see the in-flight flag immediately to skip
  // re-queueing the same upload. Otherwise every status poll caused duplicates.
  const inFlightSaves = useRef<Set<string>>(new Set());

  // Array pull / status
  const orderMutation = useOrderArrayReport();
  const fileMutation = useFetchArrayFileMutation();
  const status = useArrayReportStatus({
    orderId: order?.orderId,
    enabled: !!order,
    startedAt,
  });

  // Approval + Gobodo-backed request record
  const isApprover = useIsArrayApprover();
  const currentRequest = useCurrentArrayRequest(
    clientId ?? "",
    arrayClientKey,
    !!clientId,
  );
  const submitMutation = useSubmitArrayRequest();
  const approveMutation = useApproveArrayRequest();
  const rejectMutation = useRejectArrayRequest();
  const recallMutation = useRecallArrayRequest();
  const persistMutation = usePersistArrayMetadata();

  // Files upload
  const uploadMutation = useUploadCreditBlockFile(clientId ?? "");

  // Auto-persist Array metadata onto the approved record when a pull finishes
  useEffect(() => {
    if (!currentRequest || !order) return;
    if (currentRequest.requestStatus !== "approved") return;
    if (currentRequest.arrayOrderId === order.orderId) return;
    persistMutation.mutate({
      recordId: currentRequest.id,
      arrayOrderId: order.orderId,
      arrayReportKey: order.reportKey,
      arrayDisplayToken: order.displayToken,
      arrayFirstOrderedAt: order.firstOrderedAt,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.orderId, currentRequest?.id, currentRequest?.requestStatus]);

  const submitRequest = () => {
    if (!clientId) {
      toastError(
        "Cannot submit",
        "Client id unavailable — open from the client detail page.",
      );
      return;
    }
    submitMutation.mutate(
      { clientId, clientName, arrayClientKey },
      {
        onSuccess: (record) => {
          if (record.requestStatus === "approved") {
            success("Approved", "You can pull now.");
          } else {
            info(
              "Request submitted",
              `Awaiting approval from ${APPROVERS.join(", ")}.`,
            );
          }
        },
        onError: (err) => toastError("Submit failed", describeError(err)),
      },
    );
  };

  const placeOrder = (offCycle: boolean) => {
    setConfirmForce(false);
    setStartedAt(Date.now());
    orderMutation.mutate(
      { arrayClientKey, offCycle },
      {
        onSuccess: ({ data }) => {
          setOrder(data);
          if (data.message)
            info(
              "Using cached report",
              `First ordered ${data.firstOrderedAt ?? "recently"}`,
            );
        },
        onError: (err) => {
          const copy = errorCopy(err as ArrayError);
          toastError(copy.title, copy.detail);
        },
      },
    );
  };

  const download = (reportType: ArrayReportType) => {
    const reportId = status.data?.data.reportId;
    if (!reportId) {
      toastError("Download unavailable", "The report has no reportId yet.");
      return;
    }
    fileMutation.mutate(
      { reportId, reportType },
      {
        onSuccess: ({ data }) => {
          window.open(data.url, "_blank", "noopener,noreferrer");
          success(
            "Opened presigned URL",
            `${reportType.toUpperCase()} link is valid for ${data.expiresIn}s`,
          );
        },
        onError: (err) => {
          const copy = errorCopy(err as ArrayError);
          toastError(copy.title, copy.detail);
        },
      },
    );
  };

  const saveToFiles = async (reportType: ArrayReportType) => {
    const reportId = status.data?.data.reportId;
    const orderId = order?.orderId;
    if (!reportId || !orderId || !clientId) {
      toastError(
        "Cannot save",
        !clientId ? "Client id unavailable." : "Report is not ready yet.",
      );
      return;
    }
    const savedKey = `${orderId}:${reportType}`;
    if (savedArtifacts.has(savedKey)) {
      info(
        "Already saved",
        `${reportType.toUpperCase()} is already in the Files tab.`,
      );
      return;
    }
    // Durable dedup: if the CR record already has a file id persisted for
    // this exact orderId + report type, don't re-upload. This catches the
    // browser-reload case where the in-memory `savedArtifacts` set is empty
    // but Gobodo already has the file from a previous session. Without
    // this, every reload after a cache-hit pull duplicated both files.
    const persistedFileId =
      reportType === "pdf"
        ? currentRequest?.arrayPdfFileId
        : currentRequest?.arrayJsonFileId;
    if (persistedFileId && currentRequest?.arrayOrderId === orderId) {
      // Mirror the persisted state into the in-memory set so the auto-save
      // effect doesn't keep re-trying on subsequent renders.
      setSavedArtifacts((prev) => new Set(prev).add(savedKey));
      return;
    }
    // Synchronous guard against re-entrant saves (setState is async).
    if (inFlightSaves.current.has(savedKey)) return;
    inFlightSaves.current.add(savedKey);
    try {
      setSavingArtifact(reportType);
      const { data: fileMeta } = await fileMutation.mutateAsync({
        reportId,
        reportType,
      });
      if (!fileMeta.url) throw new Error("Array returned no presigned URL");

      // PDF: encrypt with last-4 SSN via the dev-proxy encrypt endpoint.
      // JSON: fetch raw via the passthrough download endpoint.
      let bytesResp: Response;
      if (reportType === "pdf" && ssnLast4) {
        bytesResp = await fetch("/credit/array/encrypt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: fileMeta.url, password: ssnLast4 }),
        });
      } else {
        bytesResp = await fetch(
          `/credit/array/download?url=${encodeURIComponent(fileMeta.url)}`,
        );
      }
      if (!bytesResp.ok)
        throw new Error(`fetch-through failed with ${bytesResp.status}`);
      const blob = await bytesResp.blob();
      const mime =
        reportType === "pdf" ? "application/pdf" : "application/json";
      const ext = reportType === "pdf" ? "pdf" : "json";
      const suffix =
        reportType === "pdf" && ssnLast4 ? "pdf-protected" : reportType;
      const fileName = `credit-report-array-${orderId}-${suffix}.${ext}`;
      const file = new File([blob], fileName, { type: mime });
      const uploaded = await uploadMutation.mutateAsync({
        file,
        description: `Credit Report (Array) — orderId ${orderId}${reportType === "pdf" && ssnLast4 ? " — PDF password: last-4 SSN" : ""}`,
        reltypekey: getRelationshipTypeKeyForCategory("reports") ?? undefined,
      });

      // Persist file id on the approved CreditReport__c record
      const uploadedId =
        Array.isArray(uploaded) && uploaded.length > 0
          ? String(uploaded[0].id)
          : undefined;
      if (currentRequest && uploadedId) {
        persistMutation.mutate({
          recordId: currentRequest.id,
          arrayOrderId: orderId,
          ...(reportType === "pdf"
            ? { arrayPdfFileId: uploadedId }
            : { arrayJsonFileId: uploadedId }),
        });
      }

      setSavedArtifacts((prev) => new Set(prev).add(savedKey));
      success(
        "Saved to Files",
        `${reportType.toUpperCase()} added to this client's Files tab.`,
      );
    } catch (err) {
      const msg = describeError(err);
      toastError("Save to Files failed", msg);
      // Clear the in-flight flag so the user can retry.
      inFlightSaves.current.delete(savedKey);
    } finally {
      setSavingArtifact(null);
    }
  };

  const ordering = orderMutation.isPending;
  const polling =
    status.isFetching ||
    (!!order &&
      !isReportReady(
        status.data?.data ?? {
          pdfStatus: "Not Started",
          jsonStatus: "Not Started",
        },
      ));
  const ready = !!status.data?.data && isReportReady(status.data.data);
  const statusBody = status.data?.data;
  const stageLabel = ordering
    ? "Ordering…"
    : polling && statusBody
      ? `Retrieving PDF (${statusBody.pdfStatus}), JSON (${statusBody.jsonStatus})…`
      : ready
        ? "Ready"
        : "Idle";

  // Auto-save both artifacts as soon as the report is Ready. Drive both via
  // a single async sequence so each save only kicks off once — the previous
  // implementation fired both calls synchronously before state updated,
  // producing duplicate uploads.
  useEffect(() => {
    if (!ready || !order?.orderId || !clientId) return;
    const orderId = order.orderId;
    let cancelled = false;
    (async () => {
      if (!cancelled && !savedArtifacts.has(`${orderId}:pdf`))
        await saveToFiles("pdf");
      if (!cancelled && !savedArtifacts.has(`${orderId}:json`))
        await saveToFiles("json");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, order?.orderId]);

  // --- RENDER ---

  const canPull =
    !!currentRequest &&
    (currentRequest.requestStatus === "approved" ||
      currentRequest.requestStatus === "completed");

  const submitRejection = () => {
    if (!rejectingId) return;
    rejectMutation.mutate(
      { recordId: rejectingId, reason: rejectReason || "(no reason given)" },
      {
        onSuccess: () => {
          setRejectingId(null);
          setRejectReason("");
          info("Request rejected", "");
        },
        onError: (err) => toastError("Reject failed", describeError(err)),
      },
    );
  };

  return (
    <div className="border border-blue-200 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" /> Pull via Array
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Automated credit pull for {clientName}. Cached reports reuse for 30
            days.
          </p>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          key: <code>{arrayClientKey.slice(0, 8)}…</code>
          {isApprover && (
            <span className="ml-2 inline-flex items-center gap-1 text-green-700">
              <ShieldCheck className="h-3 w-3" /> approver
            </span>
          )}
        </span>
      </div>

      {/* Approval state banner */}
      {clientId && currentRequest?.requestStatus === "pending_approval" && (
        <div className="rounded-md border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/30 p-3 space-y-2">
          <div className="flex items-start gap-2 text-sm text-yellow-900 dark:text-yellow-200">
            <Clock className="h-4 w-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Awaiting approval</div>
              <div>
                Submitted by {currentRequest.requestedBy}. Approvers:{" "}
                {APPROVERS.join(", ")} (fallback: #help-IT-Engineering).
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {isApprover ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    approveMutation.mutate(currentRequest.id, {
                      onSuccess: () =>
                        success("Approved", "Pull the report below."),
                      onError: (err) => {
                        // eslint-disable-next-line no-console
                        console.error("[Array approve] error:", err);
                        toastError("Approve failed", describeError(err));
                      },
                    })
                  }
                  disabled={approveMutation.isPending}
                  className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
                >
                  {approveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {approveMutation.isPending ? "Approving…" : "Approve"}
                </button>
                <button
                  type="button"
                  onClick={() => setRejectingId(currentRequest.id)}
                  disabled={rejectMutation.isPending}
                  className="inline-flex items-center gap-1 border border-red-600 text-red-700 hover:bg-red-50 px-3 py-1.5 rounded text-sm"
                >
                  <ShieldAlert className="h-4 w-4" /> Reject
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() =>
                  recallMutation.mutate(currentRequest.id, {
                    onSuccess: () => info("Recalled", ""),
                    onError: (err) =>
                      toastError("Recall failed", describeError(err)),
                  })
                }
                disabled={recallMutation.isPending}
                className="inline-flex items-center gap-1 text-sm text-yellow-900 dark:text-yellow-200 underline"
              >
                <X className="h-4 w-4" /> Recall request
              </button>
            )}
          </div>
        </div>
      )}

      {clientId && currentRequest?.requestStatus === "rejected" && (
        <div className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-3">
          <div className="flex items-start gap-2 text-sm text-red-800 dark:text-red-200">
            <ShieldAlert className="h-4 w-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Request rejected</div>
              {currentRequest.rejectionReason && (
                <div>Reason: {currentRequest.rejectionReason}</div>
              )}
              {currentRequest.approvedBy && (
                <div className="text-xs opacity-70">
                  By {currentRequest.approvedBy}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={submitRequest}
              className="text-sm underline"
            >
              Request again
            </button>
          </div>
        </div>
      )}

      {order?.message && (
        <div className="flex items-start justify-between rounded-md border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/30 p-3">
          <div className="text-sm text-yellow-900 dark:text-yellow-200">
            Using existing report from {order.firstOrderedAt ?? "a recent pull"}
            .
          </div>
          <button
            type="button"
            onClick={() => setConfirmForce(true)}
            className="text-sm text-yellow-900 dark:text-yellow-200 underline hover:no-underline"
          >
            Force new pull
          </button>
        </div>
      )}

      <div className="flex items-center gap-3">
        {/* No request yet → submit */}
        {clientId && !currentRequest && (
          <button
            type="button"
            onClick={submitRequest}
            disabled={submitMutation.isPending}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {submitMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isApprover ? (
              <FileText className="h-4 w-4" />
            ) : (
              <ShieldAlert className="h-4 w-4" />
            )}
            {submitMutation.isPending
              ? "Submitting…"
              : isApprover
                ? "Pull Credit Report"
                : "Request Array Credit Report"}
          </button>
        )}

        {/* Approved (or completed) + no Array pull yet this session → pull button */}
        {canPull && !order && (
          <button
            type="button"
            onClick={() => placeOrder(false)}
            disabled={ordering}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {ordering ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {ordering ? "Ordering…" : "Pull Credit Report"}
          </button>
        )}

        {order && !ready && (
          <div className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            {stageLabel}
          </div>
        )}

        {ready && (
          <div className="inline-flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
            <CheckCircle className="h-4 w-4" />
            {stageLabel}
          </div>
        )}
      </div>

      {ready && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => download("pdf")}
            disabled={fileMutation.isPending}
            className="inline-flex items-center gap-2 border border-blue-600 text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40 px-3 py-1.5 rounded text-sm"
          >
            <Download className="h-4 w-4" /> PDF{" "}
            <ExternalLink className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => download("json")}
            disabled={fileMutation.isPending}
            className="inline-flex items-center gap-2 border border-blue-600 text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40 px-3 py-1.5 rounded text-sm"
          >
            <Download className="h-4 w-4" /> JSON{" "}
            <ExternalLink className="h-3 w-3" />
          </button>
          {clientId && (
            <>
              <button
                type="button"
                onClick={() => saveToFiles("pdf")}
                disabled={savingArtifact !== null}
                className="inline-flex items-center gap-2 border border-green-600 text-green-700 hover:bg-green-100 dark:hover:bg-green-900/40 px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                {savingArtifact === "pdf" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : savedArtifacts.has(`${order?.orderId}:pdf`) ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {savedArtifacts.has(`${order?.orderId}:pdf`)
                  ? "PDF saved"
                  : ssnLast4
                    ? "Save PDF (encrypted)"
                    : "Save PDF"}
              </button>
              <button
                type="button"
                onClick={() => saveToFiles("json")}
                disabled={savingArtifact !== null}
                className="inline-flex items-center gap-2 border border-green-600 text-green-700 hover:bg-green-100 dark:hover:bg-green-900/40 px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                {savingArtifact === "json" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : savedArtifacts.has(`${order?.orderId}:json`) ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {savedArtifacts.has(`${order?.orderId}:json`)
                  ? "JSON saved"
                  : "Save JSON"}
              </button>
            </>
          )}
        </div>
      )}

      {status.isError && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-3">
          <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
          <div className="text-sm text-red-800 dark:text-red-200 flex-1">
            <div className="font-medium">
              {errorCopy(status.error as ArrayError).title}
            </div>
            <div>{errorCopy(status.error as ArrayError).detail}</div>
          </div>
          {errorCopy(status.error as ArrayError).retry === "offCycle" && (
            <button
              type="button"
              onClick={() => placeOrder(true)}
              className="inline-flex items-center gap-1 text-sm text-red-800 dark:text-red-200 underline"
            >
              <RefreshCw className="h-3 w-3" /> New pull
            </button>
          )}
        </div>
      )}

      {/* Force-pull confirmation modal */}
      {confirmForce && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Force new credit pull?
            </h4>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This bypasses the 30-day cache and places a billable new order
              with Array. Continue?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmForce(false)}
                className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => placeOrder(true)}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
              >
                Force pull
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection reason modal (approvers only) */}
      {rejectingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-3">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Reject Array request
            </h4>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (shown to the requester)"
              className="w-full border rounded p-2 text-sm dark:bg-gray-700 dark:border-gray-600"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejectingId(null);
                  setRejectReason("");
                }}
                className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRejection}
                disabled={rejectMutation.isPending}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded disabled:opacity-50"
              >
                {rejectMutation.isPending ? "Rejecting…" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArrayCreditPullPanel;
